import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { MeetingAlertComponent } from '@app/components/meeting-alert/meeting-alert.component';
import {
  getCurrentMeeting,
  hasActiveMeetingForProject,
} from '@app/components/meeting-alert/meeting-alert.helper';
import { SalesContactCardComponent } from '@app/components/sales-contact-card/sales-contact-card.component';
import { ScheduleMeetingComponent } from '@app/components/schedule-meeting/schedule-meeting.component';
import { resolveProjectStatusIds } from '@app/helpers/project-state.helper';
import { ProjectAdminResponse } from '@app/models/admin.model';
import {
  MeetingListResponse,
  MeetingResponse,
} from '@app/models/meeting.model';
import { ProjectAdminService } from '@app/services/admin/project-admin.service';
import { ToastService } from '@app/services/toast.service';
import { GenericProjectStore } from '@app/stores/projects/generic-project.store';
import { ButtonComponent } from '@app/ui-components/button/button.component';
import { DetailsPageHeaderComponent } from '@app/ui-components/details-page-header/details-page-header.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { finalize, forkJoin, map, startWith } from 'rxjs';
import { DetailsTabComponent } from './details-tab/details-tab.component';
import { ObligationsTabComponent } from './obligations-tab/obligations-tab.component';

type Tab = 'details' | 'obligations';
interface TabDef {
  key: Tab;
  label: string;
}

@Component({
  selector: 'app-project-ideas-details',
  standalone: true,
  imports: [
    CommonModule,
    ButtonComponent,
    DetailsPageHeaderComponent,
    TranslatePipe,
    DetailsTabComponent,
    SalesContactCardComponent,
    ObligationsTabComponent,
    ScheduleMeetingComponent,
    MeetingAlertComponent,
  ],
  templateUrl: './project-ideas-details.component.html',
  styleUrl: './project-ideas-details.component.scss',
})
export class ProjectIdeasDetailsComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly projectService = inject(ProjectAdminService);
  private readonly genericProjectStore = inject(GenericProjectStore);
  private readonly translate = inject(TranslateService);
  private readonly toast = inject(ToastService);

  readonly projectId = toSignal(
    this.route.paramMap.pipe(
      startWith(this.route.snapshot.paramMap),
      map((param) => param.get('projectId') ?? param.get('id') ?? ''),
    ),
    { initialValue: '' },
  );

  readonly meetings = signal<MeetingResponse[]>([]);
  readonly loadingMeetings = signal(false);

  readonly isEditing = signal(false);
  readonly isScheduleOpen = signal(false);
  readonly activeTab = signal<Tab>('details');
  readonly tabs: ReadonlyArray<TabDef> = [
    { key: 'details', label: 'admin.projectIdeas.tabs.details' },
    { key: 'obligations', label: 'admin.projectIdeas.tabs.obligations' },
  ];
  readonly project = signal<ProjectAdminResponse | null>(null);
  readonly isLoading = signal(false);
  readonly error = signal<string | null>(null);

  protected files = signal<any[]>([]);

  readonly showSalesContact = computed<boolean>(() => {
    const project = this.project();
    return project !== null && project.contactUserId === null;
  });

  readonly hasActiveMeetingForProject = computed(() =>
    hasActiveMeetingForProject(this.meetings(), this.projectId()),
  );

  readonly currentMeeting = computed(() =>
    getCurrentMeeting(this.meetings(), this.projectId()),
  );
  readonly showViewProjectButton = computed(() => {
    const state = this.project()?.state;
    if (state == null) {
      return false;
    }
    const statusIds = resolveProjectStatusIds(
      this.genericProjectStore.projectStatusEntities(),
    );
    return state === statusIds.Draft;
  });
  readonly isScheduleDisabled = computed(() => {
    const meeting = this.currentMeeting();
    return (
      this.loadingMeetings() ||
      (!!meeting && meeting.status === 2) ||
      !this.project() ||
      this.showSalesContact()
    );
  });

  ngOnInit(): void {
    this.reloadAll();
  }

  reloadAll(): void {
    const projectId = this.projectId().trim();
    if (!projectId) return;

    this.isLoading.set(true);
    this.loadingMeetings.set(true);
    this.error.set(null);

    forkJoin({
      project: this.projectService.getProjectAdminById(projectId),
      meetings: this.projectService.getMeetings({ project: projectId }),
      files: this.projectService.getFilesForProject(projectId),
    })
      .pipe(
        finalize(() => {
          this.isLoading.set(false);
          this.loadingMeetings.set(false);
        }),
      )
      .subscribe({
        next: ({ project, meetings, files }) => {
          const projectData = project as ProjectAdminResponse;
          if (!this.isProjectIdeaState(projectData.state)) {
            this.router.navigate(['/admin', 'projects', projectId], {
              replaceUrl: true,
              state: { from: '/admin/project-ideas' },
            });
            return;
          }

          this.project.set(projectData);
          this.meetings.set((meetings as MeetingListResponse)?.data ?? []);
          this.files.set(files.data);
        },
        error: () => {
          this.error.set('LOAD_FAILED');
          this.meetings.set([]);
        },
      });
  }

  reloadProject(): void {
    const id = this.projectId().trim();
    if (!id) return;

    this.isLoading.set(true);
    this.error.set(null);

    this.projectService
      .getProjectAdminById(id)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (project: ProjectAdminResponse) => {
          if (!this.isProjectIdeaState(project.state)) {
            this.router.navigate(['/admin', 'projects', id], {
              replaceUrl: true,
              state: { from: '/admin/project-ideas' },
            });
            return;
          }
          this.project.set(project);
        },
        error: () => this.error.set('LOAD_FAILED'),
      });
  }

  private isProjectIdeaState(state: number | null | undefined): boolean {
    if (state == null) {
      return false;
    }
    const statusIds = resolveProjectStatusIds(
      this.genericProjectStore.projectStatusEntities(),
    );
    return state === statusIds.Idea;
  }

  onContactAssigned(_userId: string | number): void {
    this.reloadProject();
  }

  onEdit(): void {
    this.router.navigate(['/admin', 'project-ideas', this.projectId(), 'edit']);
  }

  onViewProject(): void {
    const projectId = this.projectId().trim();
    if (!projectId) return;
    this.router.navigate(['/admin', 'projects', projectId]);
  }

  setTab(tab: Tab): void {
    this.activeTab.set(tab);
  }

  goBack(): void {
    const target = history.state?.from || '/admin/project-ideas';
    this.router.navigate([target], { relativeTo: this.route });
  }

  title(): string {
    return this.project()?.name ?? '';
  }

  onCancelMeeting(meetingId: string): void {
    const projectId = this.projectId();
    if (!projectId) return;

    this.loadingMeetings.set(true);
    this.projectService
      .cancelMeetingByContact(meetingId)
      .pipe(finalize(() => this.loadingMeetings.set(false)))
      .subscribe({
        next: () => {
          this.toast.success(
            this.translate.instant('admin.scheduleMeeting.toasts.cancelled'),
          );
          this.reloadAll();
        },
        error: () => {
          this.toast.error(
            this.translate.instant('admin.scheduleMeeting.toasts.cancelFailed'),
          );
        },
      });
  }

  onMeetingDone(meetingId?: string): void {
    const id = meetingId ?? this.currentMeeting()?.id;
    if (!id) return;

    this.loadingMeetings.set(true);
    this.projectService
      .setMeetingToDone(id)
      .pipe(finalize(() => this.loadingMeetings.set(false)))
      .subscribe({
        next: () => {
          this.toast.success(
            this.translate.instant('admin.scheduleMeeting.toasts.markedDone'),
          );
          this.reloadAll();
        },
        error: () => {
          this.toast.error(
            this.translate.instant(
              'admin.scheduleMeeting.toasts.markDoneFailed',
            ),
          );
        },
      });
  }
}
